import React, { useEffect, useRef, useState } from 'react';
import styles from '../static/styles/BundleTaskSubmit.module.css';
import { submitBundleTaskEntry } from '../api/bundle';
import eslCoinIcon from '../static/img/DailyCheckin/eslCoin.png';

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const buildSteps = (task, bundle) => [
  `Open ${bundle?.game ? bundle.game.replace(/ MOBILE$/i, ' Mobile') : 'the game'} and start a match`,
  task.challengeObjective,
  'Submit your screenshot below for verification',
];

const BundleTaskSubmit = ({ task, bundle, onClose, onSubmitted }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isSubmitting, onClose]);

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = '';
    if (!selected) return;

    if (!ACCEPTED_TYPES.includes(selected.type)) {
      setFile(null);
      setError('Please choose a JPG or PNG image.');
      return;
    }
    if (selected.size > MAX_IMAGE_BYTES) {
      setFile(null);
      setError(`That image is ${formatBytes(selected.size)}. Please choose one under 2 MB.`);
      return;
    }

    setError('');
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!file || isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    const response = await submitBundleTaskEntry(task.challengeId, file);

    if (response?.success) {
      onSubmitted(response.message || 'Challenge submitted!');
    } else {
      setError(response?.message || 'Submission failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  const theme = bundle?.theme || {};
  const themeStyle = {
    '--modal-bg': theme.modalBg,
    '--modal-challenge-card': theme.modalChallengeCard,
    '--modal-reward-box': theme.modalRewardBox,
    '--modal-reward-border': theme.modalRewardBorder,
    '--modal-reward-glow': theme.modalRewardGlow,
    '--modal-panel': theme.modalPanel,
    '--modal-page': theme.page,
    '--modal-accent': theme.accent,
    '--modal-cta': theme.splashButton,
    '--modal-cta-glow': theme.splashButtonGlow,
    '--modal-cta-text': theme.splashButtonText,
  };

  return (
    <div
      className={styles.overlay}
      style={themeStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">

        <div className={styles.card}>

          {/* HEADER */}
          <div className={styles.header}>
            <img className={styles.headerCoin} src={eslCoinIcon} alt="" />
            <h2 className={styles.headerTitle}>{bundle?.taskTitle || 'TASK'}</h2>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              disabled={isSubmitting}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* CHALLENGE */}
          <div className={styles.challengeCard}>
            <div className={styles.challengeTop}>
              <img
                className={styles.challengeThumb}
                src={task.challengeThumbnail}
                alt=""
                onError={(e) => { e.currentTarget.style.opacity = '0'; }}
              />
              <div className={styles.challengeText}>
                <span className={styles.challengeLabel}>TODAY'S CHALLENGE</span>
                <h3 className={styles.challengeTitle}>{task.challengeObjective}</h3>
              </div>
            </div>

            <div className={styles.rewardBox}>
              <img className={styles.rewardCoin} src={eslCoinIcon} alt="" />
              <div className={styles.rewardText}>
                <span className={styles.rewardLabel}>WIN REWARD</span>
                <strong className={styles.rewardValue}>+{task.challengePrize} Credits</strong>
              </div>
            </div>
          </div>

          {/* STEPS */}
          <h4 className={styles.sectionTitle}>HOW TO COMPLETE</h4>
          <ol className={styles.steps}>
            {buildSteps(task, bundle).map((step, index) => (
              <li key={index} className={styles.step}>
                <span className={styles.stepNum}>{index + 1}</span>
                <span className={styles.stepText}>{step}</span>
              </li>
            ))}
          </ol>

          {/* UPLOAD */}
          <button
            type="button"
            className={styles.dropzone}
            onClick={() => inputRef.current?.click()}
            disabled={isSubmitting}
          >
            <span className={styles.cameraCircle}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M3 8.5A1.5 1.5 0 014.5 7h2.2l1.2-2h8.2l1.2 2h2.2A1.5 1.5 0 0121 8.5v9A1.5 1.5 0 0119.5 19h-15A1.5 1.5 0 013 17.5v-9z" strokeLinejoin="round" />
                <circle cx="12" cy="13" r="3.4" />
              </svg>
            </span>
            <span className={styles.uploadTitle}>TAP TO UPLOAD</span>
            <span className={styles.uploadHint}>JPG or PNG &middot; max 2 MB &middot; 1080p recommended</span>
          </button>

          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png"
            onChange={handleFileChange}
            hidden
          />

          {file && (
            <div className={styles.attached}>
              <span className={styles.attachedIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="16" rx="2.5" />
                  <path d="M3 16l4.5-4.5 4 4 3-3L21 17" strokeLinejoin="round" />
                </svg>
              </span>
              <span className={styles.attachedText}>
                <span className={styles.attachedName}>{file.name}</span>
                <span className={styles.attachedMeta}>{formatBytes(file.size)} &middot; attached</span>
              </span>
              <button
                type="button"
                className={styles.attachedRemove}
                onClick={() => setFile(null)}
                disabled={isSubmitting}
                aria-label="Remove file"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          )}

          {error && <p className={styles.error}>{error}</p>}
        </div>

      </div>

      {/* CTA - pinned to the bottom of the viewport, outside the scrolling sheet */}
      <div className={styles.ctaBar}>
        <button
          type="button"
          className={styles.cta}
          onClick={handleSubmit}
          disabled={!file || isSubmitting}
        >
          {isSubmitting ? 'SENDING...' : 'SEND FOR REVIEW →'}
        </button>
      </div>
    </div>
  );
};

export default BundleTaskSubmit;
